import { defineMessages } from '@/i18n'

/** Fleet: vehicles, reservations, check-out and check-in (docs/fleet-design.md). */
export const fleetMessages = defineMessages({
  en: {
    title: 'Fleet',
    subtitle: "Vehicles from AU's register: who has them, who has booked them, and every check-out.",
    loading: 'Loading fleet',
    loadingVehicle: 'Loading vehicle',
    notFound: 'Vehicle not found.',
    tileAvailable: 'Available',
    tileReserved: 'Reserved today',
    tileOut: 'Out now',
    tileLeasing: { one: 'Leasing · {count} vehicle / month', other: 'Leasing · {count} vehicles / month' },
    search: 'Plate, vehicle, driver, crew…',
    all: 'All',
    statusAvailable: 'Available',
    statusReserved: 'Reserved',
    statusOut: 'Out',
    shownOf: '{shown} of {total} vehicles',
    colPlate: 'Plate',
    colVehicle: 'Vehicle',
    colStatus: 'Status',
    colWith: 'With',
    colTuv: 'TÜV (AU)',
    colFinancing: 'Financing',
    leasing: 'Leasing',
    month: 'month',
    owned: 'Owned',
    registerNote: "Vehicles, TÜV and financing are read from AU's vehicle register; correct them there. Bookings and check-outs are kept here.",
    withDriver: 'Out with {name}',
    nextFrom: 'Booked from {date}',
    boardEmpty: 'No bookings or check-outs in the next two weeks.',
    boardTitle: 'Next two weeks',
    legendReserved: 'Reserved',
    legendOut: 'Checked out',
    tuvPastHint: 'Past date as recorded in AU — often not maintained there.',
    fuel: 'Fuel',
    odometer: 'Odometer',
    outWith: 'Out with {name}',
    since: 'since {date}',
    checkIn: 'Check in',
    checkOut: 'Check out',
    reserve: 'Reserve',
    bookings: 'Bookings',
    noBookings: 'No upcoming bookings.',
    cancelBooking: 'Cancel booking',
    keep: 'Keep',
    trips: 'Check-outs',
    noTrips: 'Never checked out in this app yet.',
    stillOut: 'still out',
    damageReported: 'Damage reported',
    project: 'Project',
    crew: 'Crew',
    reserveHint: 'Book the vehicle for a project or a crew.',
    cancel: 'Cancel',
    save: 'Save',
    'from': 'From',
    to: 'To',
    purpose: 'Purpose (optional)',
    photos: 'Photos',
    sideFront: 'Front',
    sideBack: 'Back',
    sideLeft: 'Left',
    sideRight: 'Right',
    forBooking: 'Booking {from} – {to}',
    driver: 'Driver',
    note: 'Note (optional)',
    kmDriven: '{km} km driven',
    leftWith: 'Left with {km} km',
    damageNew: 'New damage',
    damageWhat: 'What is damaged?',
  },
  de: {
    title: 'Fuhrpark',
    subtitle: 'Fahrzeuge aus dem AU-Register: wer sie hat, wer sie gebucht hat, und jede Ausgabe.',
    loading: 'Fuhrpark wird geladen',
    loadingVehicle: 'Fahrzeug wird geladen',
    notFound: 'Fahrzeug nicht gefunden.',
    tileAvailable: 'Verfügbar',
    tileReserved: 'Heute gebucht',
    tileOut: 'Unterwegs',
    tileLeasing: { one: 'Leasing · {count} Fahrzeug / Monat', other: 'Leasing · {count} Fahrzeuge / Monat' },
    search: 'Kennzeichen, Fahrzeug, Fahrer, Kolonne…',
    all: 'Alle',
    statusAvailable: 'Verfügbar',
    statusReserved: 'Gebucht',
    statusOut: 'Unterwegs',
    shownOf: '{shown} von {total} Fahrzeugen',
    colPlate: 'Kennzeichen',
    colVehicle: 'Fahrzeug',
    colStatus: 'Status',
    colWith: 'Bei',
    colTuv: 'TÜV (AU)',
    colFinancing: 'Finanzierung',
    leasing: 'Leasing',
    month: 'Monat',
    owned: 'Eigentum',
    registerNote: 'Fahrzeuge, TÜV und Finanzierung kommen aus dem AU-Fahrzeugregister; Korrekturen dort. Buchungen und Ausgaben werden hier geführt.',
    withDriver: 'Unterwegs mit {name}',
    nextFrom: 'Gebucht ab {date}',
    boardEmpty: 'Keine Buchungen oder Ausgaben in den nächsten zwei Wochen.',
    boardTitle: 'Nächste zwei Wochen',
    legendReserved: 'Gebucht',
    legendOut: 'Ausgegeben',
    tuvPastHint: 'Vergangenes Datum laut AU — dort oft nicht gepflegt.',
    fuel: 'Tank',
    odometer: 'Kilometerstand',
    outWith: 'Unterwegs mit {name}',
    since: 'seit {date}',
    checkIn: 'Rücknahme',
    checkOut: 'Ausgabe',
    reserve: 'Buchen',
    bookings: 'Buchungen',
    noBookings: 'Keine anstehenden Buchungen.',
    cancelBooking: 'Buchung stornieren',
    keep: 'Behalten',
    trips: 'Ausgaben',
    noTrips: 'In dieser App noch nie ausgegeben.',
    stillOut: 'noch unterwegs',
    damageReported: 'Schaden gemeldet',
    project: 'Projekt',
    crew: 'Kolonne',
    reserveHint: 'Das Fahrzeug für ein Projekt oder eine Kolonne buchen.',
    cancel: 'Abbrechen',
    save: 'Speichern',
    'from': 'Von',
    to: 'Bis',
    purpose: 'Zweck (optional)',
    photos: 'Fotos',
    sideFront: 'Vorne',
    sideBack: 'Hinten',
    sideLeft: 'Links',
    sideRight: 'Rechts',
    forBooking: 'Buchung {from} – {to}',
    driver: 'Fahrer',
    note: 'Notiz (optional)',
    kmDriven: '{km} km gefahren',
    leftWith: 'Ausgegeben mit {km} km',
    damageNew: 'Neuer Schaden',
    damageWhat: 'Was ist beschädigt?',
  },
  cnr: {
    title: 'Vozni park',
    subtitle: 'Vozila iz AU registra: ko ih ima, ko ih je rezervisao, i svako zaduženje.',
    loading: 'Učitavanje voznog parka',
    loadingVehicle: 'Učitavanje vozila',
    notFound: 'Vozilo nije pronađeno.',
    tileAvailable: 'Slobodna',
    tileReserved: 'Rezervisana danas',
    tileOut: 'Na terenu',
    tileLeasing: { one: 'Lizing · {count} vozilo / mjesečno', few: 'Lizing · {count} vozila / mjesečno', other: 'Lizing · {count} vozila / mjesečno' },
    search: 'Tablice, vozilo, vozač, kolona…',
    all: 'Sva',
    statusAvailable: 'Slobodno',
    statusReserved: 'Rezervisano',
    statusOut: 'Na terenu',
    shownOf: '{shown} od {total} vozila',
    colPlate: 'Tablice',
    colVehicle: 'Vozilo',
    colStatus: 'Status',
    colWith: 'Kod',
    colTuv: 'TÜV (AU)',
    colFinancing: 'Finansiranje',
    leasing: 'Lizing',
    month: 'mjesečno',
    owned: 'Vlasništvo',
    registerNote: 'Vozila, TÜV i finansiranje čitaju se iz AU registra vozila; ispravke tamo. Rezervacije i zaduženja vode se ovdje.',
    withDriver: 'Zadužio: {name}',
    nextFrom: 'Rezervisano od {date}',
    boardEmpty: 'Nema rezervacija ni zaduženja u naredne dvije nedjelje.',
    boardTitle: 'Naredne dvije nedjelje',
    legendReserved: 'Rezervisano',
    legendOut: 'Zaduženo',
    tuvPastHint: 'Prošli datum prema AU — tamo se često ne ažurira.',
    fuel: 'Gorivo',
    odometer: 'Kilometraža',
    outWith: 'Zadužio: {name}',
    since: 'od {date}',
    checkIn: 'Razduži',
    checkOut: 'Zaduži',
    reserve: 'Rezerviši',
    bookings: 'Rezervacije',
    noBookings: 'Nema predstojećih rezervacija.',
    cancelBooking: 'Otkaži rezervaciju',
    keep: 'Zadrži',
    trips: 'Zaduženja',
    noTrips: 'U ovoj aplikaciji još nije zaduženo.',
    stillOut: 'još na terenu',
    damageReported: 'Prijavljeno oštećenje',
    project: 'Projekat',
    crew: 'Kolona',
    reserveHint: 'Rezervišite vozilo za projekat ili kolonu.',
    cancel: 'Otkaži',
    save: 'Sačuvaj',
    'from': 'Od',
    to: 'Do',
    purpose: 'Svrha (neobavezno)',
    photos: 'Fotografije',
    sideFront: 'Naprijed',
    sideBack: 'Pozadi',
    sideLeft: 'Lijevo',
    sideRight: 'Desno',
    forBooking: 'Rezervacija {from} – {to}',
    driver: 'Vozač',
    note: 'Napomena (neobavezno)',
    kmDriven: 'Pređeno {km} km',
    leftWith: 'Zaduženo sa {km} km',
    damageNew: 'Novo oštećenje',
    damageWhat: 'Šta je oštećeno?',
  },
  bs: {
    title: 'Vozni park',
    subtitle: 'Vozila iz AU registra: ko ih ima, ko ih je rezervisao, i svako zaduženje.',
    loading: 'Učitavanje voznog parka',
    loadingVehicle: 'Učitavanje vozila',
    notFound: 'Vozilo nije pronađeno.',
    tileAvailable: 'Slobodna',
    tileReserved: 'Rezervisana danas',
    tileOut: 'Na terenu',
    tileLeasing: { one: 'Lizing · {count} vozilo / mjesečno', few: 'Lizing · {count} vozila / mjesečno', other: 'Lizing · {count} vozila / mjesečno' },
    search: 'Tablice, vozilo, vozač, kolona…',
    all: 'Sva',
    statusAvailable: 'Slobodno',
    statusReserved: 'Rezervisano',
    statusOut: 'Na terenu',
    shownOf: '{shown} od {total} vozila',
    colPlate: 'Tablice',
    colVehicle: 'Vozilo',
    colStatus: 'Status',
    colWith: 'Kod',
    colTuv: 'TÜV (AU)',
    colFinancing: 'Finansiranje',
    leasing: 'Lizing',
    month: 'mjesečno',
    owned: 'Vlasništvo',
    registerNote: 'Vozila, TÜV i finansiranje čitaju se iz AU registra vozila; ispravke tamo. Rezervacije i zaduženja vode se ovdje.',
    withDriver: 'Zadužio: {name}',
    nextFrom: 'Rezervisano od {date}',
    boardEmpty: 'Nema rezervacija ni zaduženja u naredne dvije sedmice.',
    boardTitle: 'Naredne dvije sedmice',
    legendReserved: 'Rezervisano',
    legendOut: 'Zaduženo',
    tuvPastHint: 'Prošli datum prema AU — tamo se često ne ažurira.',
    fuel: 'Gorivo',
    odometer: 'Kilometraža',
    outWith: 'Zadužio: {name}',
    since: 'od {date}',
    checkIn: 'Razduži',
    checkOut: 'Zaduži',
    reserve: 'Rezerviši',
    bookings: 'Rezervacije',
    noBookings: 'Nema predstojećih rezervacija.',
    cancelBooking: 'Otkaži rezervaciju',
    keep: 'Zadrži',
    trips: 'Zaduženja',
    noTrips: 'U ovoj aplikaciji još nije zaduženo.',
    stillOut: 'još na terenu',
    damageReported: 'Prijavljeno oštećenje',
    project: 'Projekat',
    crew: 'Kolona',
    reserveHint: 'Rezervišite vozilo za projekat ili kolonu.',
    cancel: 'Otkaži',
    save: 'Spasi',
    'from': 'Od',
    to: 'Do',
    purpose: 'Svrha (neobavezno)',
    photos: 'Fotografije',
    sideFront: 'Naprijed',
    sideBack: 'Pozadi',
    sideLeft: 'Lijevo',
    sideRight: 'Desno',
    forBooking: 'Rezervacija {from} – {to}',
    driver: 'Vozač',
    note: 'Napomena (neobavezno)',
    kmDriven: 'Pređeno {km} km',
    leftWith: 'Zaduženo sa {km} km',
    damageNew: 'Novo oštećenje',
    damageWhat: 'Šta je oštećeno?',
  },
  sr: {
    title: 'Vozni park',
    subtitle: 'Vozila iz AU registra: ko ih ima, ko ih je rezervisao, i svako zaduženje.',
    loading: 'Učitavanje voznog parka',
    loadingVehicle: 'Učitavanje vozila',
    notFound: 'Vozilo nije pronađeno.',
    tileAvailable: 'Slobodna',
    tileReserved: 'Rezervisana danas',
    tileOut: 'Na terenu',
    tileLeasing: { one: 'Lizing · {count} vozilo / mesečno', few: 'Lizing · {count} vozila / mesečno', other: 'Lizing · {count} vozila / mesečno' },
    search: 'Tablice, vozilo, vozač, kolona…',
    all: 'Sva',
    statusAvailable: 'Slobodno',
    statusReserved: 'Rezervisano',
    statusOut: 'Na terenu',
    shownOf: '{shown} od {total} vozila',
    colPlate: 'Tablice',
    colVehicle: 'Vozilo',
    colStatus: 'Status',
    colWith: 'Kod',
    colTuv: 'TÜV (AU)',
    colFinancing: 'Finansiranje',
    leasing: 'Lizing',
    month: 'mesečno',
    owned: 'Vlasništvo',
    registerNote: 'Vozila, TÜV i finansiranje čitaju se iz AU registra vozila; ispravke tamo. Rezervacije i zaduženja vode se ovde.',
    withDriver: 'Zadužio: {name}',
    nextFrom: 'Rezervisano od {date}',
    boardEmpty: 'Nema rezervacija ni zaduženja u naredne dve nedelje.',
    boardTitle: 'Naredne dve nedelje',
    legendReserved: 'Rezervisano',
    legendOut: 'Zaduženo',
    tuvPastHint: 'Prošli datum prema AU — tamo se često ne ažurira.',
    fuel: 'Gorivo',
    odometer: 'Kilometraža',
    outWith: 'Zadužio: {name}',
    since: 'od {date}',
    checkIn: 'Razduži',
    checkOut: 'Zaduži',
    reserve: 'Rezerviši',
    bookings: 'Rezervacije',
    noBookings: 'Nema predstojećih rezervacija.',
    cancelBooking: 'Otkaži rezervaciju',
    keep: 'Zadrži',
    trips: 'Zaduženja',
    noTrips: 'U ovoj aplikaciji još nije zaduženo.',
    stillOut: 'još na terenu',
    damageReported: 'Prijavljeno oštećenje',
    project: 'Projekat',
    crew: 'Kolona',
    reserveHint: 'Rezervišite vozilo za projekat ili kolonu.',
    cancel: 'Otkaži',
    save: 'Sačuvaj',
    'from': 'Od',
    to: 'Do',
    purpose: 'Svrha (neobavezno)',
    photos: 'Fotografije',
    sideFront: 'Napred',
    sideBack: 'Pozadi',
    sideLeft: 'Levo',
    sideRight: 'Desno',
    forBooking: 'Rezervacija {from} – {to}',
    driver: 'Vozač',
    note: 'Napomena (neobavezno)',
    kmDriven: 'Pređeno {km} km',
    leftWith: 'Zaduženo sa {km} km',
    damageNew: 'Novo oštećenje',
    damageWhat: 'Šta je oštećeno?',
  },
})
